import { deriveImageUrl, deriveImageThumbUrl } from './recipeHelpers';
import { toMinutes } from './recipeTime';

// Normalize various instruction shapes into an array of step strings.
const toLines = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val
      .map(x => (typeof x === 'string' ? x : (x && typeof x === 'object' ? (x.text || '') : '')))
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    const lines = trimmed.includes('\n') ? trimmed.split(/\r?\n/) : trimmed.split(/(?<=\.)\s+/);
    return lines.map(s => s.trim()).filter(Boolean);
  }
  return [];
};

// Normalize a recipe / weekly-plan document into the RecipeDetail UI shape used by the Meal
// Plan screen. Weekly-plan docs store a per-week `serves` alongside base `servings`, so this
// prefers `serves` (serves-first precedence) and carries the extra image/scaling fields the
// Meal Plan detail needs. This is intentionally a SUPERSET of, and different in precedence
// from, the shared normalizeRecipeDetail in ./normalizeRecipeDetail.js — do not merge them
// without preserving serves-first ordering and the extra fields. Moved verbatim from
// MealPlan.jsx (Step 9 "safe partial"): behavior-preserving relocation of a pure function.
export const normalizeWeeklyPlanRecipe = (raw) => {
  const image = deriveImageUrl(raw);
  const ingredients = Array.isArray(raw.ingredients) ? raw.ingredients.map((ing) => {
    const name = ing.name || ing.ingredient || '';
    const unit = ing.unit || ing.units || '';
    const q = ing.quantity;
    let quantityStr = '';
    if (typeof q === 'number') {
      quantityStr = unit ? `${q} ${unit}` : `${q}`;
    } else if (typeof q === 'string') {
      quantityStr = unit ? `${q} ${unit}` : q;
    } else {
      quantityStr = unit ? `${unit}` : '';
    }
    return { name, quantity: quantityStr.trim(), category: ing.category || undefined };
  }) : [];
  // Baseline ingredients preserved as numeric for exact scaling
  const baseIngredients = Array.isArray(raw.baseIngredients) ? raw.baseIngredients.map((ing) => ({
    name: ing.name || ing.ingredient || '',
    unit: ing.unit || ing.units || '',
    quantity: typeof ing.quantity === 'number' ? ing.quantity : (Number(ing.quantity) || 0),
    category: ing.category || undefined,
  })) : undefined;
  const methodCandidates = [raw.method, raw.instructions, raw.recipeInstructions, raw.directions, raw.steps];
  let method = [];
  for (const c of methodCandidates) {
    const lines = toLines(c);
    if (lines.length) { method = lines; break; }
  }
  return {
    id: raw._id || raw.id,
    name: raw.name || raw.title || '',
    image,
    imageThumb: deriveImageThumbUrl(raw),
    imagePath: raw.imagePath || raw.recipe?.imagePath || '',
    imageThumbPath: raw.imageThumbPath || raw.thumbnailPath || raw.thumbPath || raw.recipe?.imageThumbPath || '',
    source: raw.source || raw.sourceUrl || '',
    prepTime: toMinutes(raw.prepTime),
    cookTime: toMinutes(raw.cookTime),
    totalTime: toMinutes(raw.totalTime),
    mealType: raw.mealType || '',
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    // Weekly plan docs store per-week 'serves' alongside base 'servings'; prefer serves for UI sync
    servings: Number(raw.serves ?? raw.servings) || 1,
    baseServings: Number(raw.baseServings ?? raw.servings ?? raw.serves) || undefined,
    unitSystem: raw.unitSystem || 'metric',
    ingredientsScaledForServes: raw.ingredientsScaledForServes === true,
    ingredients,
    baseIngredients,
    method,
    nutrition: raw.nutrition || { calories: 0, protein: 0, carbs: 0, fat: 0 },
  };
};
